import { useCallback } from 'react';
import { useLazyHasVersionChanges } from './history/useHistoryRange';
import VerifyPostModal from './VerifyPostModal';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { UIActions } from '../ui/uiSlice';
import useNavigate from '../utils/router/useNavigate';
import postPath from '../utils/url/postPath';

const USE_START_REVERIFICATION_POST_FRAGMENT = gql(`
  fragment useStartReverification_post on Post {
    id
    ...VerifyPostModal_post
    ...postPath_post
    verificationEntries {
      id
      version
    }
  }
`);

function useStartReverification(
  post: DocumentType<typeof USE_START_REVERIFICATION_POST_FRAGMENT> | undefined,
) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const versionTo = useAppSelector((state) => state.editor.version);
  const [hasVersionChanges, { loading }] = useLazyHasVersionChanges();

  const startReverification = useCallback(async () => {
    if (!post) return;
    const versionFrom = post.verificationEntries.at(0)?.version ?? versionTo;
    const hasChanges = await hasVersionChanges({
      postId: post.id,
      versionFrom,
      versionTo,
    });
    if (hasChanges) {
      navigate(`${postPath(post)}/verify`);
    } else {
      dispatch(UIActions.openModal(VerifyPostModal, { post, noChanges: true }));
    }
  }, [dispatch, hasVersionChanges, navigate, post, versionTo]);

  return {
    startReverification,
    loading,
  };
}

export default useStartReverification;
